import { c as _c } from "react-compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import { Metrics } from 'collaboration-sidebar/utils/metrics';
import debounce from '../../utils/debounce';
import ChildrenErrorBoundary from './ChildrenErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
const TIME_TO_RESET = 3000;
function useRenderCount() {
  'use no memo';

  const renderCount = useRef(0);
  const debouncedReset = useMemo(() => debounce(() => {
    renderCount.current = 0;
  }, TIME_TO_RESET), []);
  useEffect(() => {
    renderCount.current += 1;
    debouncedReset();
  });

  // React compiler does not like accessing the ref during render
  return renderCount.current;
}

/**
 * From: https://git.hubteam.com/HubSpot/Critsit/issues/4039
 * This is a somehow hacky approach but could be useful for us to remediate errors when ErrorBoundary is not enough.
 *
 * This component will render its children until a certain threshold and then it'll stop rendering and notify us in Grafana.
 *
 * It also uses ChildrenErrorBoundary to fall back to rendering the app's original children when an error is thrown
 * from inside collaboration sidebar's components.
 */
export default function ErrorCountWrapper(t0) {
  "use memo";

  const $ = _c(13);
  const {
    appName,
    children,
    errorBoundaryFingerprint,
    threshold,
    renderFallback
  } = t0;
  const renderCount = useRenderCount();
  const hasRenderedTooMuch = renderCount > threshold;
  let t1;
  let t2;
  if ($[0] !== appName || $[1] !== errorBoundaryFingerprint || $[2] !== hasRenderedTooMuch) {
    t1 = () => {
      if (!hasRenderedTooMuch) {
        return;
      }
      console.error(`[${appName}] ${errorBoundaryFingerprint} rendered too many times`);
      Metrics.counter("infinite-loop", {
        app_name: appName
      }).increment();
      Raven.captureException(new Error(`[${appName}] ${errorBoundaryFingerprint} rendered too many times`), {
        extra: {
          appName
        },
        fingerprint: [errorBoundaryFingerprint],
        tags: {
          lib: "collaboration-sidebar"
        }
      });
    };
    t2 = [appName, errorBoundaryFingerprint, hasRenderedTooMuch];
    $[0] = appName;
    $[1] = errorBoundaryFingerprint;
    $[2] = hasRenderedTooMuch;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (hasRenderedTooMuch) {
    let t3;
    if ($[5] !== renderFallback) {
      t3 = renderFallback();
      $[5] = renderFallback;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  if ($[7] !== renderFallback) {
    t3 = renderFallback();
    $[7] = renderFallback;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== errorBoundaryFingerprint || $[11] !== t3) {
    t4 = /*#__PURE__*/_jsx(ChildrenErrorBoundary, {
      fingerprint: errorBoundaryFingerprint,
      originalChildren: t3,
      children: children
    });
    $[9] = children;
    $[10] = errorBoundaryFingerprint;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}